import {
  useCurrentWorkspace,
  useFindByQuery,
  useProduct,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import {
  LoadingIndicator,
  RoutineModule,
  RoutineModuleButton,
  SearchInput,
  SlideAction,
  Spacing,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useNavigate } from 'react-router-dom';

type ProductRoutinesAllColumnProps = {
  productId: string;
};

export const ProductRoutinesAllColumn = ({
  productId,
}: ProductRoutinesAllColumnProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentWorkspace = useCurrentWorkspace();
  const slideLayoutContext = useSlideLayoutSafe();
  const { product, isLoading } = useProduct(productId);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch workflow data to detect alerts
  const { data: workflowData } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: {
      filter: {
        product: { eq: productId },
        and: { disabled: { ne: true } },
      },
    },
    options: { enabled: !!currentWorkspace },
  });

  const sortedRoutines = useMemo(() => {
    if (!product?.routines) return [];
    return [...product.routines].sort(
      (a: WorkspaceRoutine, b: WorkspaceRoutine) =>
        (a.order ?? 0) - (b.order ?? 0)
    );
  }, [product?.routines]);

  const filteredRoutines = useMemo(() => {
    if (!searchTerm) return sortedRoutines;
    const lower = searchTerm.toLowerCase();
    return sortedRoutines.filter((r: WorkspaceRoutine) => {
      const name = r.name || r.config?.routineButtonText || '';
      return name.toLowerCase().includes(lower);
    });
  }, [sortedRoutines, searchTerm]);

  const getRoutineWorkflows = useCallback(
    (id: string) => {
      if (!id || !workflowData?.items?.length) return [];
      return workflowData.items.filter(
        (item: any) => item.trigger?.config?.workspaceRoutineId === id
      );
    },
    [workflowData]
  );

  const onRoutineClick = (
    routine: WorkspaceRoutine,
    module: RoutineModule<any, any>
  ) => {
    if (!routine?.id) return;
    slideLayoutContext?.prepareSlideTransition(SlideAction.NEXT);
    navigate(`/products/${productId}/routines/${routine.id}`, {
      state: {
        from: 'routines-list',
        slideLayoutInitialTransition: SlideAction.NEXT,
      },
    });
  };

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <View style={styles.container}>
      <View style={styles.searchContainer}>
        <SearchInput
          value={searchTerm}
          onChangeText={setSearchTerm}
          placeholder={t('assets.routineSearchPlaceholder')}
          height={40}
        />
      </View>
      <ScrollView style={styles.scrollView}>
        {filteredRoutines.map((routine: WorkspaceRoutine, index: number) => {
          const workflows = getRoutineWorkflows(routine.id ?? '');
          return (
            <RoutineModuleButton
              key={routine.id}
              routine={routine}
              buttonIndex={index}
              buttonCount={filteredRoutines.length}
              workflows={workflows}
              onClick={onRoutineClick}
            />
          );
        })}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: Spacing.basePadding.paddingHorizontal,
    flex: 1,
  },
  searchContainer: {
    paddingBottom: Spacing.basePadding.base,
  },
  scrollView: {
    flex: 1,
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
      }
    }
  }
`;
